import { useMemo } from 'react';
import type { PracticeQueueRouteParams } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/project-sidebar-navigation-resource/types.tsx';
import type { UseResourceCustomContext } from '@atlassian/jira-resource-with-custom-router-context/src/controllers/types.tsx';
import { useResourceWithCustomRouterContext } from '@atlassian/jira-resource-with-custom-router-context/src/controllers/use-resource-with-custom-router-context/index.tsx';
import { projectContextResourceWithCache } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useServiceDeskContext } from '../servicedesk-context/index.tsx';

const getProjectContextResource = () => projectContextResourceWithCache;

export const usePageContext = (
	customContext?: UseResourceCustomContext<PracticeQueueRouteParams>,
) => {
	const tenantContext = useTenantContext();

	const {
		data: projectContext,
		loading: projectContextLoading,
		error: projectContextError,
	} = useResourceWithCustomRouterContext(getProjectContextResource(), customContext);

	const [{ data: serviceDeskContext, loading: serviceDeskLoading, error: serviceDeskError }] =
		useServiceDeskContext(projectContext?.projectKey || undefined, customContext);

	const pageContextData = useMemo(
		() => ({
			data:
				projectContext && serviceDeskContext
					? {
							serviceDeskContext: {
								portalId: serviceDeskContext.portalId,
								portalUrl: serviceDeskContext.portalUrl,
								canSeeNewIssueView: serviceDeskContext.canSeeNewIssueView,
								userRole: serviceDeskContext.userRole,
								serviceDeskId: serviceDeskContext.serviceDeskId,
							},

							projectContext: {
								projectId: projectContext.projectId,
								projectKey: projectContext.projectKey,
								projectName: projectContext.projectName,
								projectType: projectContext.projectType,
								// Refactor this in https://jdog.jira-dev.com/browse/JSK-443 this is a temporary fix to
								// resolve a regression https://jira.atlassian.com/browse/JSDCLOUD-8751
								isAdmin: projectContext.isProjectAdmin || tenantContext.isAdmin,
								isProjectSimplified: projectContext.simplified,
							},

							tenantContext: {
								locale: tenantContext.locale,
								userFullname: tenantContext.userFullname,
								cloudId: tenantContext.cloudId,
								fabricPfDirUrl: tenantContext.fabricPfDirUrl,
								atlassianAccountId: tenantContext.atlassianAccountId,
								isJiraAdmin: tenantContext.isAdmin,
								isSiteAdmin: tenantContext.isSiteAdmin,
								activationId: tenantContext.activationId,
							},
						}
					: null,
			loading: projectContextLoading || serviceDeskLoading,
			error: serviceDeskError || projectContextError || null,
		}),
		[
			tenantContext,
			projectContext,
			serviceDeskContext,
			projectContextLoading,
			serviceDeskLoading,
			serviceDeskError,
			projectContextError,
		],
	);

	return [pageContextData] as const;
};
